import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { RolePicker } from "./Signup";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { rankOptions } from "../mock";
import { Ship, Loader2, Briefcase } from "lucide-react";
import { useToast } from "../hooks/use-toast";

// New Google users choose their role here before entering the feed.
const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading, completeProfile } = useAuth();

  const [role, setRole] = useState("seafarer");
  const [rank, setRank] = useState("");
  const [organization, setOrganization] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && user.role) navigate("/feed");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await completeProfile({ role, rank, organization, job_title: jobTitle });
      navigate("/feed");
    } catch (err) {
      toast({ title: "Could not save", description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const isSeafarer = role === "seafarer";
  const orgLabel = role === "institute" ? "Institute name" : "Company name";

  return (
    <AuthLayout
      title={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}!`}
      subtitle="Tell us who you are so we can tailor Indosbook for you."
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-1.5">
          <Label>I am joining as</Label>
          <RolePicker value={role} onChange={setRole} />
        </div>

        {isSeafarer ? (
          <div className="space-y-1.5">
            <Label>Rank</Label>
            <Select value={rank} onValueChange={setRank}>
              <SelectTrigger className="h-12"><SelectValue placeholder="Select your rank" /></SelectTrigger>
              <SelectContent className="max-h-56">
                {rankOptions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="org">{orgLabel}</Label>
              <div className="relative">
                <Ship className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input id="org" required value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder={orgLabel} className="h-12 pl-10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="job-title">Your Job Title / Role</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input id="job-title" required value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} data-testid="onboarding-job-title"
                  placeholder={role === "institute" ? "e.g. Admissions Head, Principal" : "e.g. Crewing Manager, HR Executive"} className="h-12 pl-10" />
              </div>
            </div>
          </>
        )}

        <Button type="submit" disabled={submitting} className="h-12 w-full rounded-full bg-[#FF4500] text-base font-bold text-white hover:bg-[#e03d00]">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue to Indosbook"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Onboarding;
